/**
 * useSyntaxVisualization - Hook para visualización de métricas Space Syntax
 *
 * Este hook gestiona la visualización de métricas de sintaxis espacial
 * en el mapa, aplicando rampas de color a los edges según la métrica
 * seleccionada (Integration o Choice).
 *
 * Usa feature-state de MapLibre para aplicar los valores de sintaxis
 * de forma eficiente sin modificar el source GeoJSON.
 *
 * @example
 * ```typescript
 * // En el componente MapLayout o similar
 * useSyntaxVisualization();
 *
 * // El hook automáticamente:
 * // 1. Detecta cuando syntaxModeActive = true
 * // 2. Aplica métricas via setFeatureState
 * // 3. Aplica rampa de color interpolada
 * // 4. Restaura colores originales cuando se desactiva
 * ```
 */

/* eslint-disable max-lines-per-function, complexity, @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useCallback } from 'react';
import { mapEngine } from '../../map-engine/MapEngineService';
import {
  COLOR_RAMPS,
  DEFAULT_SYNTAX_RAMP,
  type SyntaxRampName,
} from '../config/syntax-color-ramps';
import { useNetworkStyle } from '../contexts/NetworkStyleContext';
import { useSyntaxAnalysisSafe, type SyntaxMetricType } from '../contexts/SyntaxAnalysisContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const EDGES_LAYER_ID = 'canonical-network-edges';
const SOURCE_ID = 'canonical-network';

/**
 * Nombre del feature-state donde se almacena el valor de sintaxis normalizado
 */
const SYNTAX_VALUE_STATE = 'syntaxValue';

// ============================================================================
// HOOK
// ============================================================================

export interface UseSyntaxVisualizationConfig {
  /** Rampa de color a usar (default: spectral) */
  colorRamp?: SyntaxRampName;
}

export interface UseSyntaxVisualizationResult {
  /** Si la visualización de sintaxis está activa */
  isActive: boolean;
  /** Métrica actualmente visualizada */
  currentMetric: SyntaxMetricType | null;
}

/**
 * Hook para visualización de métricas Space Syntax con rampas de color
 *
 * Usa feature-state de MapLibre para aplicar valores de sintaxis de forma
 * eficiente. Requiere que el source tenga promoteId configurado.
 */
export function useSyntaxVisualization(
  config: UseSyntaxVisualizationConfig = {}
): UseSyntaxVisualizationResult {
  const { colorRamp = DEFAULT_SYNTAX_RAMP } = config;

  // Contextos
  const syntaxContext = useSyntaxAnalysisSafe();
  const { style } = useNetworkStyle();

  // Refs para evitar re-renders innecesarios
  const isActiveRef = useRef(false);
  const lastMetricRef = useRef<SyntaxMetricType | null>(null);
  const appliedEdgeIdsRef = useRef<Set<string>>(new Set());

  // Extraer valores del contexto de sintaxis
  const syntaxModeActive = syntaxContext?.syntaxModeActive ?? false;
  const selectedMetric = syntaxContext?.selectedMetric ?? 'integration_global';
  const slimMetrics = syntaxContext?.slimMetrics ?? [];
  const currentMetricVisualRange = syntaxContext?.currentMetricVisualRange ?? null;
  const contrastExponent = syntaxContext?.contrastExponent ?? 1;
  const status = syntaxContext?.status ?? 'idle';

  /**
   * Aplica slim metrics al mapa usando setFeatureState.
   *
   * Cada slim metric ya contiene solo `{ edgeId, value }` para la métrica
   * seleccionada — no hay lookup de columna necesario.
   */
  const applyMetricsViaFeatureState = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[useSyntaxVisualization] No map available');
      return;
    }

    if (!map.getSource(SOURCE_ID)) {
      console.warn('[useSyntaxVisualization] Source not found:', SOURCE_ID);
      return;
    }

    if (!currentMetricVisualRange) {
      console.warn('[useSyntaxVisualization] No range available');
      return;
    }

    const min = currentMetricVisualRange.min;
    const max = currentMetricVisualRange.max;
    const rangeSpan = max - min || 1;
    const newAppliedIds = new Set<string>();
    let appliedCount = 0;

    for (const entry of slimMetrics) {
      if (entry.value === null || entry.value === undefined) continue;

      const clampedValue = Math.min(Math.max((entry.value - min) / rangeSpan, 0), 1);
      const normalizedValue = Math.pow(clampedValue, contrastExponent);

      try {
        map.setFeatureState(
          { source: SOURCE_ID, id: entry.edgeId },
          { [SYNTAX_VALUE_STATE]: normalizedValue }
        );
        newAppliedIds.add(entry.edgeId);
        appliedCount++;
      } catch {
        // Ignorar errores silenciosamente (feature puede no existir aún)
      }
    }

    // Limpiar feature-states de edges que ya no tienen métricas
    for (const oldId of appliedEdgeIdsRef.current) {
      if (!newAppliedIds.has(oldId)) {
        try {
          map.removeFeatureState({ source: SOURCE_ID, id: oldId }, SYNTAX_VALUE_STATE);
        } catch {
          // Ignorar
        }
      }
    }

    appliedEdgeIdsRef.current = newAppliedIds;

    console.log('[useSyntaxVisualization] ✓ Feature-state applied to', appliedCount, 'edges', {
      metric: selectedMetric,
      range: { min, max },
      contrastExponent,
    });
  }, [contrastExponent, currentMetricVisualRange, slimMetrics, selectedMetric]);

  /**
   * Aplica la rampa de color al layer de edges usando feature-state
   */
  const applyColorRamp = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getLayer(EDGES_LAYER_ID)) {
      console.warn('[useSyntaxVisualization] Layer not found:', EDGES_LAYER_ID);
      return;
    }

    const ramp = COLOR_RAMPS[colorRamp];

    // Crear expresión de interpolación usando feature-state
    // El valor está normalizado [0, 1], así que usamos los stops directamente
    const colorExpr: any[] = [
      'interpolate',
      ['linear'],
      ['coalesce', ['feature-state', SYNTAX_VALUE_STATE], 0],
      // Agregar stops de la rampa (ya están en [0, 1])
      ...ramp.stops.flatMap(s => [s.stop, s.color]),
    ];

    // Expresión final que preserva estados de hover/selected con fallback a rampa de sintaxis
    const finalExpr = [
      'case',
      ['boolean', ['feature-state', 'snapped'], false],
      '#2196F3',
      ['boolean', ['feature-state', 'selected'], false],
      '#2FA4B5',
      ['boolean', ['feature-state', 'hover'], false],
      '#60D0E8',
      // Default: usar interpolación de sintaxis (feature-state basado)
      colorExpr,
    ];

    map.setPaintProperty(EDGES_LAYER_ID, 'line-color', finalExpr);

    console.log('[useSyntaxVisualization] ✓ Color ramp applied:', {
      metric: selectedMetric,
      ramp: colorRamp,
      stopsCount: ramp.stops.length,
    });
  }, [selectedMetric, colorRamp]);

  /**
   * Limpia todos los feature-states de sintaxis
   */
  const clearFeatureStates = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getSource(SOURCE_ID)) return;

    for (const edgeId of appliedEdgeIdsRef.current) {
      try {
        map.removeFeatureState({ source: SOURCE_ID, id: edgeId }, SYNTAX_VALUE_STATE);
      } catch (e) {
        // Ignorar
      }
    }
    appliedEdgeIdsRef.current.clear();

    console.log('[useSyntaxVisualization] ✓ Feature-states cleared');
  }, []);

  /**
   * Restaura el color original del layer de edges
   */
  const restoreOriginalColor = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getLayer(EDGES_LAYER_ID)) return;

    // Restaurar expresión original de color (como en useCanonicalNetworkLayer)
    const originalColorExpr = [
      'case',
      ['boolean', ['feature-state', 'associated'], false],
      '#f59e0b',
      ['boolean', ['feature-state', 'snapped'], false],
      '#2196F3',
      ['boolean', ['feature-state', 'selected'], false],
      '#2FA4B5',
      ['boolean', ['feature-state', 'hover'], false],
      '#60D0E8',
      style.edges.color,
    ];

    map.setPaintProperty(EDGES_LAYER_ID, 'line-color', originalColorExpr);

    // Limpiar feature-states de sintaxis
    clearFeatureStates();

    console.log('[useSyntaxVisualization] ✓ Original color restored');
  }, [style.edges.color, clearFeatureStates]);

  // ── Efecto principal: activar/desactivar visualización ──
  useEffect(() => {
    if (
      syntaxModeActive &&
      status === 'ready' &&
      slimMetrics.length > 0 &&
      currentMetricVisualRange
    ) {
      applyMetricsViaFeatureState();
      applyColorRamp();
      isActiveRef.current = true;
      lastMetricRef.current = selectedMetric;

      console.log('[useSyntaxVisualization] Syntax visualization ACTIVATED');
    } else if (!syntaxModeActive && isActiveRef.current) {
      restoreOriginalColor();
      isActiveRef.current = false;
      lastMetricRef.current = null;

      console.log('[useSyntaxVisualization] Syntax visualization DEACTIVATED');
    }
  }, [
    syntaxModeActive,
    status,
    slimMetrics,
    currentMetricVisualRange,
    selectedMetric,
    applyMetricsViaFeatureState,
    applyColorRamp,
    restoreOriginalColor,
  ]);

  // ── Efecto: cambio de métrica o contraste mientras está activo ──
  // slimMetrics ya fue actualizado por el contexto cuando la métrica cambió
  useEffect(() => {
    if (syntaxModeActive && isActiveRef.current && lastMetricRef.current !== selectedMetric) {
      applyMetricsViaFeatureState();
      applyColorRamp();
      lastMetricRef.current = selectedMetric;
      console.log('[useSyntaxVisualization] Metric changed to:', selectedMetric);
    }
  }, [
    contrastExponent,
    currentMetricVisualRange,
    selectedMetric,
    slimMetrics,
    syntaxModeActive,
    applyMetricsViaFeatureState,
    applyColorRamp,
  ]);

  // ── Cleanup: restaurar color al desmontar ──
  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        restoreOriginalColor();
      }
    };
  }, [restoreOriginalColor]);

  return {
    isActive: syntaxModeActive && status === 'ready' && slimMetrics.length > 0,
    currentMetric: syntaxModeActive ? selectedMetric : null,
  };
}

export default useSyntaxVisualization;
